import { type FieldColSpan, type FormColumns } from './types/dynamic-form.types';

/**
 * The form layout vocabulary — the whole of it.
 *
 * Named `*_CLASSES` exactly as the three control families are: the class law
 * (U19) inspects `cva()` literals, `className` props and `*_CLASSES`
 * initialisers and nothing else.
 *
 * **The layout answers to its CONTAINER, never to the window.** A form does not
 * know how wide the browser is; it knows how wide the box it was dropped into
 * is, and those are different numbers in a 420px dialog, a 960px dialog and a
 * 280px settings column. Everything below queries `@container/form`, declared
 * once on the fields region by `FORM_REGION_CLASSES`.
 *
 * Three things this file must never do, all of them guarded by
 * `dynamic-form.tokens.spec.ts`:
 *
 *  - **No interpolation.** Tailwind scans source TEXT, so a computed
 *    `` `@fits-${n}/form:grid-cols-${n}` `` compiles to nothing at all — no
 *    build error, no failing render, and a form that silently stays at one
 *    column. Every class here is a static literal, which is also why the two
 *    ramps are written out rather than generated.
 *  - **No unnamed container query.** `@fits-2:` resolves to whichever ancestor
 *    happens to be the nearest container, so a future nested one would retarget
 *    every variant in the family. Always `@fits-2/form:`.
 *  - **No dimension.** Not an `h-*`, not a `max-w-*` (W3). A form column's width
 *    is the container's business; `min-w-0` is the absence of a floor, not a
 *    size.
 *
 * `tailwind-merge` needs no teaching here: a container query is a MODIFIER, and
 * `grid-cols-*` / `col-span-*` are stock scales it already knows, so
 * `cn('grid-cols-1', '@fits-2/form:grid-cols-2')` keeps both. (The trap that
 * caught `2xs` in P10.2a and `text-code` in P10.2b was a new token in an
 * existing GROUP, which is a different thing.)
 */

/* ── the region ─────────────────────────────────────────────────────────── */

/**
 * The fields region: the one container every ramp below queries.
 *
 * `w-full` is not decoration. `container-type: inline-size` removes the
 * contents' contribution to intrinsic inline size, so a shrink-to-fit parent — a
 * `flex` row, an `inline-block` — would measure this box as zero and every
 * container query would answer false forever.
 */
export const FORM_REGION_CLASSES = '@container/form flex w-full flex-col gap-xl';

/** The form root: the region, then whatever the caller put after it. */
export const FORM_ROOT_CLASSES = 'flex flex-col gap-xl';

/** Messages nothing on screen owns — a server rejection naming an absent path. */
export const FORM_ERROR_SUMMARY_CLASSES =
  'flex flex-col gap-xs rounded-md bg-destructive/10 p-md text-caption text-destructive';

export const FORM_ACTIONS_CLASSES = 'flex items-center justify-end gap-md';

/* ── a section ──────────────────────────────────────────────────────────── */

/**
 * A `<fieldset>`, reset.
 *
 * A UA stylesheet gives every fieldset a border, an inline margin and block
 * padding, and a legend its own inline padding — all of which are the browser's
 * idea of a form from 1997. `border-0 p-0` are logical-safe (they name no side).
 *
 * `min-w-0` is load-bearing twice over: a fieldset is `min-width: min-content`
 * by UA rule, and a grid item defaults to `min-width: auto`, so without it one
 * long `dir="ltr"` identifier inside a 4-column grid widens the section past its
 * container and takes the whole form with it.
 */
export const FORM_SECTION_CLASSES = 'flex min-w-0 flex-col gap-md border-0 p-0';

/**
 * A collapsible section's body. The fieldset's own `gap-md` cannot reach inside
 * `CollapsibleContent`, which is one element rather than the section's children.
 */
export const FORM_SECTION_BODY_CLASSES = 'flex flex-col gap-md';

/**
 * An object group's body — and a **second `@container/form`, deliberately**.
 *
 * A CSS container name resolves to the NEAREST matching ancestor, so every
 * `@fits-N/form:` variant inside an object group answers to the group's own box
 * instead of to the whole fields region. That is the honest answer: a four-field
 * legal entity inside a 280px settings scope column must lay out in one column
 * while the same group in a 960px dialog opens two, and the outer region's width
 * says nothing about either.
 *
 * It is an element of its own rather than the grid itself, because a container
 * query is answered by an ANCESTOR container: a grid that declared the container
 * it queries would be asking itself and would never match.
 *
 * `w-full` for the reason `FORM_REGION_CLASSES` carries it — `container-type:
 * inline-size` takes the contents out of the intrinsic width calculation.
 */
export const FORM_OBJECT_BODY_CLASSES = '@container/form flex w-full flex-col';

/**
 * The legend's row — with a hairline under it.
 *
 * The rule it fixes came from the first stills: a legend styled like a field
 * label IS a field label to the eye, so three sections read as three stray bold
 * words among the fields rather than as three groups. The line is what says a
 * group starts here; the heavier text is what says it is a name rather than a
 * value.
 */
export const FORM_LEGEND_CLASSES =
  'flex w-full items-center gap-sm border-b border-border p-0 pb-sm';

export const FORM_LEGEND_TEXT_CLASSES = 'text-label font-semibold text-foreground';

/** The section carries an invalid field — said in colour as well as in an icon. */
export const FORM_LEGEND_INVALID_CLASSES = 'text-destructive';

export const FORM_SECTION_DESCRIPTION_CLASSES = 'text-caption text-muted-foreground';

/** The disclosure that folds a section: the section's NAME is the control. */
export const FORM_SECTION_TRIGGER_CLASSES =
  'flex flex-1 items-center gap-sm rounded-sm text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export const FORM_SECTION_CHEVRON_CLASSES =
  'shrink-0 text-muted-foreground transition-transform duration-fast ease-standard';

export const FORM_SECTION_CHEVRON_OPEN_CLASSES = 'rotate-180';

/* ── the grid ───────────────────────────────────────────────────────────── */

export const FORM_GRID_CLASSES = 'grid grid-cols-1 items-start gap-lg';

/**
 * The column ramp. The index is the section's CEILING, never a demand — the
 * container decides how much of it is taken.
 */
export const FORM_COLUMNS_CLASSES: Record<FormColumns, string> = {
  1: '',
  2: '@fits-2/form:grid-cols-2',
  3: '@fits-2/form:grid-cols-2 @fits-3/form:grid-cols-3',
  4: '@fits-2/form:grid-cols-2 @fits-3/form:grid-cols-3 @fits-4/form:grid-cols-4',
};

/** One field's cell: the label, the control, and what explains them. */
export const FORM_CELL_CLASSES = 'flex min-w-0 flex-col gap-sm';

/**
 * A boolean field's own row: the name, then the switch at the inline end.
 *
 * A switch stacked under its label reads as a control that lost its name — a
 * 44px toggle alone on a line, with the sentence that explains it hovering
 * above. Every other boolean surface in the kit (`ToggleGroup`'s rows, the
 * list's status cells) already pairs them on one line; the first stills of this
 * phase are what showed the form did not.
 *
 * ADJACENT, not `justify-between`: a list row spreads its two ends because the
 * row is the width of the table, but a form cell can be a whole 300px column —
 * and the second stills showed exactly that, a name at one edge and its switch
 * at the other with nothing in between.
 */
export const FORM_BOOLEAN_ROW_CLASSES = 'flex items-center gap-md';

/**
 * The span ramp — the SAME thresholds as the column ramp, which is the entire
 * clamp.
 *
 * A span-3 field is one column below `fits-2`, two between `fits-2` and
 * `fits-3`, three only where the grid already has three tracks. A span can
 * therefore never exceed the track count, at any width, with no measurement and
 * no `ResizeObserver` — and the two ramps are asserted to switch at the same
 * three points, because the day they disagree is the day a field overflows its
 * own grid.
 */
export const FORM_SPAN_CLASSES: Record<FieldColSpan, string> = {
  1: '',
  2: '@fits-2/form:col-span-2',
  3: '@fits-2/form:col-span-2 @fits-3/form:col-span-3',
  4: '@fits-2/form:col-span-2 @fits-3/form:col-span-3 @fits-4/form:col-span-4',
  full: 'col-span-full',
};

/* ── the field cell's furniture ─────────────────────────────────────────── */

/** A control that names itself gets a caption, not a second competing label. */
export const FORM_CAPTION_CLASSES =
  'flex flex-wrap items-center gap-sm text-label font-medium text-foreground';

/** The `<Label>`'s own row, once it may carry a locale chip beside the text. */
export const FORM_LABEL_CLASSES = 'flex flex-wrap items-center gap-sm';

export const FORM_REQUIRED_MARK_CLASSES = 'text-destructive';

export const FORM_DESCRIPTION_CLASSES = 'text-caption text-muted-foreground';

/* ── the token rail ─────────────────────────────────────────────────────── */

/**
 * The row of insertable values under a text control.
 *
 * It wraps rather than scrolls: a rail that scrolls hides the parameter an
 * operator has not thought of yet, which is the one the rail exists to show.
 */
export const FORM_TOKEN_RAIL_CLASSES = 'flex flex-wrap items-center gap-xs';

export const FORM_TOKEN_CHIP_CLASSES =
  'inline-flex items-center gap-2xs rounded-full border border-border bg-secondary px-sm py-2xs text-tiny font-medium text-secondary-foreground transition-colors duration-fast ease-standard hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50';

export const FORM_TOKEN_CHIP_REQUIRED_CLASSES = 'text-destructive';

export const FORM_TOKEN_HINT_CLASSES = 'text-muted-foreground';

export const FORM_MESSAGE_CLASSES = 'text-caption text-destructive';

/* ── the locale bar ─────────────────────────────────────────────────────── */

/**
 * The chip that says which locale a field is being edited in.
 *
 * Translatable fields now sit IN PLACE among fields that have no locale at all,
 * so the tab strip alone no longer says which controls it governs — this does.
 * Its text joins the field's accessible name, so the answer is the same for a
 * screen reader as it is on screen.
 */
export const FORM_LOCALE_MARK_CLASSES =
  'shrink-0 rounded-full bg-secondary px-sm py-2xs text-tiny font-medium text-secondary-foreground';

/** The tab strip's own row: the switch, and what it is a switch for. */
export const FORM_LOCALE_BAR_CLASSES = 'flex flex-wrap items-center gap-sm';

export const FORM_LOCALE_BAR_LABEL_CLASSES = 'text-caption text-muted-foreground';
